import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthDavesaV2Service } from '../auth-davesa-v2.service';
import { EulaService } from '../eula.service';
import { hasUserAcceptedEula, isOrganizationUser, getUserEulaVersionAccepted } from '../../utils/user-utils';

/**
 * EULA Guard - Enforces End-User License Agreement acceptance with version tracking
 * 
 * This guard ensures that regular users (non-organization users) cannot access 
 * any protected routes without accepting the current active EULA version.
 * 
 * Security Features:
 * 1. Checks EULA acceptance status on every route navigation for regular users
 * 2. Validates that user accepted the current active version
 * 3. Forces re-acceptance when EULA is updated
 * 4. Redirects to dedicated acceptance page if not accepted or version mismatch
 * 5. Prevents bypass through direct URL navigation
 * 6. Organization users bypass EULA requirement
 * 
 * @returns true if EULA accepted and version matches, redirects to /eula-acceptance if not
 */
export const EulaGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthDavesaV2Service);
  const eulaService = inject(EulaService);
  const router = inject(Router);
  
  const user = authService.loginUser();
  
  // If no user, allow (handled by auth guard)
  if (!user) {
    return true;
  }
  
  // Organization users bypass EULA requirement
  if (isOrganizationUser(user)) {
    return true;
  }
  
  // Don't check EULA if user is already on the eula-acceptance page
  if (state.url.startsWith('/eula-acceptance')) {
    return true;
  }
  
  // Check if user has accepted EULA at all
  if (!hasUserAcceptedEula(user)) {
    // User has never accepted EULA
    router.navigate(['/eula-acceptance'], { 
      queryParams: { 
        redirectUrl: state.url
      },
      // Use replaceUrl to prevent back button from returning to protected route
      replaceUrl: true
    });
    return false;
  }
  
  // Get active EULA version from backend (with caching)
  try {
    // Fetch EULA content (uses cache if available)
    await eulaService.fetchEulaContent();
    const activeVersion = eulaService.eulaVersion();
    
    // Check if user's accepted version matches active version
    const userAcceptedVersion = getUserEulaVersionAccepted(user);
    if (activeVersion && userAcceptedVersion !== activeVersion.version) {
      // EULA has been updated - force re-acceptance
      router.navigate(['/eula-acceptance'], { 
        queryParams: { 
          redirectUrl: state.url,
          previousVersion: userAcceptedVersion
        },
        // Use replaceUrl to prevent back button from returning to protected route
        replaceUrl: true
      });
      return false;
    }
    
    // Version matches - allow access
    return true;
  } catch (error) {
    // If we can't fetch active version, allow access (fail open)
    // This prevents blocking users if backend is temporarily unavailable
    console.warn('[EulaGuard] Failed to verify EULA version, allowing access', error);
    return true;
  }
};

